import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { tablesApi, reservationsApi } from '@verlo/shared/services/api';
import { floorTables, seatable } from '../../lib/tonight';
import { Button } from '@verlo/shared/components/ui/button';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { moveOptions, minutesLeft } from '../../lib/moveTable';
import { clearOrderDraft, moveOrderDraft } from '../../lib/orderDraft';

/**
 * Move a party to another table (mock: https://claude.ai/artifact/6m2gKs5oYZWBz28ihPjypy,
 * layout A). Free tables that seat them, by room, the best fit marked; the ones that won't
 * take them below, with why. Everything goes with the party: the order, the allergy, the
 * time left, the booking, the tickets the kitchen and bar are working from (server:
 * `POST /tables/{id}/move/{to}`), and the items keyed but not sent. No manager: a move
 * changes no money.
 */
const today = () => new Date().toLocaleDateString('en-CA');
const money = (n) => `£${Number(n || 0).toFixed(2)}`;

const MoveTableDialog = ({ open, onOpenChange, table, orderTotal, onMoved }) => {
  const [tables, setTables] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChosen(null);
    setTables(null);
    Promise.all([tablesApi.getAll(), reservationsApi.getByDate(today()).catch(() => ({ data: [] }))])
      // Not a table out of use tonight, and a join as the one table it is (lib/tonight.js).
      .then(([t, r]) => { setTables(floorTables(t.data || []).filter(seatable)); setBookings(r.data || []); })
      .catch(() => { setTables([]); toast.error("Couldn't load the tables"); });
  }, [open]);

  const { free, taken, best } = useMemo(
    () => (tables ? moveOptions(table, tables, bookings) : { free: [], taken: [], best: null }),
    [table, tables, bookings],
  );
  // By room, in the floor's own order; a table with no room is in "Main".
  const rooms = useMemo(() => {
    const out = new Map();
    for (const o of free) {
      const room = o.table.section || 'Main';
      if (!out.has(room)) out.set(room, []);
      out.get(room).push(o);
    }
    return [...out.entries()];
  }, [free]);

  const pick = free.find((o) => o.table.id === chosen);
  const left = minutesLeft(table);
  const covers = table?.covers || 0;
  // The order page knows its order even when its copy of the table predates it.
  const hasOrder = Boolean(table?.current_order_id || orderTotal);
  const what = [
    `${covers} guest${covers === 1 ? '' : 's'}`,
    hasOrder ? `the order${orderTotal ? ` (${money(orderTotal)})` : ''}` : null,
    table?.has_allergies ? 'the allergy' : null,
    `the ${left} minutes left`,
  ].filter(Boolean);
  const whatWords = what.length > 1 ? `${what.slice(0, -1).join(', ')} and ${what[what.length - 1]}` : what[0];

  const move = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      await tablesApi.moveTable(table.id, pick.table.id);
      // Their unsent items go with them. Anything parked under the free table was left by a
      // party long gone.
      clearOrderDraft(pick.table.id);
      moveOrderDraft(table.id, pick.table.id);
      // The boards redraw with the new table; paper already on the rail cannot.
      toast.success(hasOrder
        ? `Moved to ${pick.table.name}. Kitchen and bar screens show ${pick.table.name}; printed tickets still say ${table.name}.`
        : `Moved to ${pick.table.name}`);
      onOpenChange(false);
      onMoved?.(pick.table);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't move the table");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent size="sheet" className="max-h-[90vh] flex flex-col gap-0 p-0" data-testid="move-table-dialog">
        <DialogHeader className="px-6 pt-5 pb-2">
          <DialogTitle>Move {table?.name} to…</DialogTitle>
          <DialogDescription>
            {covers} guest{covers === 1 ? '' : 's'}. Free tables that seat them.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-3">
          {tables === null && <p className="py-6 text-sm text-ink-faint">Loading the floor…</p>}
          {tables !== null && free.length === 0 && (
            <Notice tone="warn" word="Full" className="my-3">No free table seats {covers} right now.</Notice>
          )}
          {rooms.map(([room, opts]) => (
            <div key={room}>
              <div className="mt-3 mb-1.5 flex items-center gap-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                {room}<span className="h-px flex-1 bg-hairline" />
              </div>
              {opts.map((o) => (
                <button
                  key={o.table.id}
                  type="button"
                  onClick={() => setChosen(o.table.id)}
                  aria-pressed={chosen === o.table.id}
                  className={`mb-1.5 grid w-full grid-cols-[52px_64px_minmax(0,1fr)_auto] items-center gap-2.5 border bg-white px-3 py-2.5 text-left
                    ${chosen === o.table.id ? 'border-2 border-verlo bg-verlo-tint px-[11px] py-[9px]' : 'border-hairline hover:border-hairline-strong'}`}
                  data-testid={`move-to-${o.table.name}`}
                >
                  <span className="font-heading text-[16px] font-extrabold text-ink">{o.table.name}</span>
                  <span className="font-heading text-[13px] font-semibold text-ink-soft">{o.table.capacity} seats</span>
                  <span className="min-w-0 truncate text-[13px] text-ink-soft">{o.note}</span>
                  <span>
                    {o.warn
                      ? <Signal tone="warn" dot={false}>Booked {o.booking.time}</Signal>
                      : o.table.id === best ? <Signal tone="info" dot={false}>Best fit</Signal>
                        : <Signal tone="ok" dot={false}>Fits</Signal>}
                  </span>
                </button>
              ))}
            </div>
          ))}
          {taken.length > 0 && (
            <div>
              <div className="mt-3 mb-1.5 flex items-center gap-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                Too small, or taken<span className="h-px flex-1 bg-hairline" />
              </div>
              {taken.map((o) => (
                <div key={o.table.id} className="mb-1.5 grid grid-cols-[52px_64px_minmax(0,1fr)] items-center gap-2.5 border border-hairline bg-white px-3 py-2 opacity-60">
                  <span className="font-heading text-[15px] font-extrabold text-ink">{o.table.name}</span>
                  <span className="font-heading text-[13px] font-semibold text-ink-soft">{o.table.capacity} seats</span>
                  <span className="min-w-0 truncate text-[13px] text-ink-soft">{o.why}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-hairline bg-white px-6 pt-3 pb-4">
          {pick?.warn && (
            <Notice tone="warn" word="Booked" className="mb-2.5" data-testid="move-booking-warning">
              {pick.booking.customer_name} ({pick.booking.party_size}) is booked on {pick.table.name} at {pick.booking.time}.
              {' '}{table?.name}'s party has {left} minutes left.
            </Notice>
          )}
          <p className="mb-3 text-[13px] text-ink-soft">
            {pick ? <><b className="text-ink">To {pick.table.name}:</b> {whatWords}.</> : <>Choose a table. <b className="text-ink">Everything goes with them:</b> {whatWords}.</>}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={move} disabled={!pick || busy} data-testid="move-confirm">
              {busy ? 'Moving…' : pick ? `Move to ${pick.table.name}` : 'Move'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MoveTableDialog;
